/**
 * NotificationDrawer — full-height right drawer for the bell (replaces the
 * old `w-96 max-h-96` NotificationDropdown per ux-overhaul-hld.md §7). Built
 * on the shared AppDrawer chassis (§8a) — the first real consumer, proving
 * the pattern before Phase 3's create/edit forms reuse it. Data layer and
 * row markup are untouched: same useNotifications/useUnreadCount/
 * useMarkRead/useMarkAllRead hooks, same NotificationItem component.
 */
import { BellIcon } from '@heroicons/react/24/outline';
import { AppDrawer } from '@/features/_shell/AppDrawer';
import {
  useNotifications,
  useUnreadCount,
  useMarkRead,
  useMarkAllRead,
} from '@/features/notifications/api';
import { NotificationItem } from './NotificationItem';

const PAGE_SIZE = 30;

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationDrawer({ open, onClose }: NotificationDrawerProps) {
  const {
    data: notificationsResult,
    isLoading: isNotificationsLoading,
    isError: isNotificationsError,
  } = useNotifications({ page: 1, perPage: PAGE_SIZE });
  const { data: unreadCountResult } = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const notifications = notificationsResult?.data ?? [];
  const total = notificationsResult?.meta?.total ?? notifications.length;
  const unreadCount = unreadCountResult?.count ?? 0;
  const hasMore = total > notifications.length;

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      width="md"
      title="Notifications"
      description={unreadCount > 0 ? `${unreadCount} new` : undefined}
    >
      <div data-testid="notification-drawer-body">
        {open && (
          <div className="-mx-6 -mt-6 mb-4 flex items-center justify-end border-b border-gray-100 px-6 py-3">
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={unreadCount === 0 || markAllRead.isPending}
              data-testid="notification-mark-all-read"
              className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 disabled:cursor-not-allowed disabled:text-gray-300"
            >
              Mark all as read
            </button>
          </div>
        )}

        {isNotificationsLoading ? (
          <div className="p-12 text-center" data-testid="notification-drawer-loading">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="mt-3 text-sm text-gray-500">Loading notifications…</p>
          </div>
        ) : isNotificationsError ? (
          <div className="p-12 text-center" data-testid="notification-drawer-error">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <BellIcon className="h-6 w-6 text-red-400" />
            </div>
            <h3 className="mt-3 text-sm font-medium text-gray-900">Couldn&apos;t load notifications</h3>
            <p className="mt-1 text-sm text-gray-500">Please try again in a moment.</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center" data-testid="notification-drawer-empty">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <BellIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="mt-3 text-sm font-medium text-gray-900">You&apos;re all caught up</h3>
            <p className="mt-1 text-sm text-gray-500">New notifications will appear here.</p>
          </div>
        ) : (
          <div className="-mx-6 divide-y divide-gray-100" data-testid="notification-drawer-list">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={(id) => markRead.mutate(id)}
                isMarkingRead={markRead.isPending}
                onClose={onClose}
              />
            ))}
            {hasMore && (
              <div className="px-6 py-3 text-center text-xs text-gray-400" data-testid="notification-drawer-more">
                Older notifications
              </div>
            )}
          </div>
        )}
      </div>
    </AppDrawer>
  );
}
